import { ESV_API_BASE_URL, ESV_API_KEY, getBookById, getBookByName, BIBLE_BOOKS } from '../config/bibleConfig'
import { BibleVerse, BibleReference, ESVApiResponse, BibleSearchResult } from '../types/bible'

// Simple in-memory cache
const cache = new Map<string, { data: any; timestamp: number }>()
const CACHE_DURATION = 1000 * 60 * 60 // 1 hour

function getCached(key: string): any | null {
  const cached = cache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data
  }
  return null
}

function setCache(key: string, data: any): void {
  cache.set(key, { data, timestamp: Date.now() })
}

/**
 * Parse a Bible reference string (e.g., "Genesis 1:1-5", "John 3:16", or "Ephesians 5")
 */
export function parseReference(reference: string): BibleReference | null {
  const normalized = reference.trim()
  
  // Pattern: BookName Chapter:Verse or BookName Chapter:StartVerse-EndVerse or BookName Chapter
  const pattern = /^(\d*\s*[a-zA-Z]+)\s+(\d+)(?::(\d+)(?:-(\d+))?)?$/i
  const match = normalized.match(pattern)
  
  if (!match) {
    return null
  }
  
  const bookName = match[1].trim()
  const chapter = parseInt(match[2], 10)
  const startVerse = match[3] ? parseInt(match[3], 10) : undefined
  const endVerse = match[4] ? parseInt(match[4], 10) : undefined
  
  const book = getBookByName(bookName)
  if (!book) {
    return null
  }
  
  return {
    bookId: book.id,
    bookName: book.name,
    chapter,
    startVerse,
    endVerse,
  }
}

/**
 * Format a Bible reference for ESV API
 */
function formatReferenceForAPI(ref: BibleReference): string {
  const book = getBookById(ref.bookId)
  if (!book) return ''
  
  let query = `${book.name} ${ref.chapter}`
  if (ref.startVerse) {
    query += `:${ref.startVerse}`
    if (ref.endVerse && ref.endVerse !== ref.startVerse) {
      query += `-${ref.endVerse}`
    }
  }
  return query
}

/**
 * Fetch a passage from ESV API
 */
export async function getPassage(reference: string): Promise<string | null> {
  if (!ESV_API_KEY) {
    console.warn('ESV API key not configured. Please set VITE_ESV_API_KEY in your .env file')
    return null
  }
  
  const cacheKey = `passage:${reference}`
  const cached = getCached(cacheKey)
  if (cached) {
    return cached
  }
  
  try {
    const params = new URLSearchParams({
      q: reference,
      'include-headings': 'false',
      'include-footnotes': 'false',
      'include-verse-numbers': 'true',
      'include-short-copyright': 'false',
      'include-passage-references': 'false',
    })
    
    const response = await fetch(`${ESV_API_BASE_URL}/text/?${params}`, {
      headers: {
        'Authorization': `Token ${ESV_API_KEY}`,
      },
    })
    
    if (!response.ok) {
      throw new Error(`ESV API error: ${response.status} ${response.statusText}`)
    }
    
    const data: ESVApiResponse = await response.json()
    const passage = data.passages?.[0]?.trim() || null
    
    if (passage) {
      setCache(cacheKey, passage)
    }
    
    return passage
  } catch (error) {
    console.error('Error fetching passage from ESV API:', error)
    return null
  }
}

/**
 * Get a single verse
 */
export async function getVerse(bookId: string, chapter: number, verse: number): Promise<BibleVerse | null> {
  const book = getBookById(bookId)
  if (!book) return null
  
  const reference = `${book.name} ${chapter}:${verse}`
  const text = await getPassage(reference)
  
  if (!text) return null
  
  return {
    bookId,
    chapter,
    verse,
    text: text.replace(/\d+\s+/g, '').trim(), // Remove verse numbers from text
  }
}

/**
 * Get a full chapter
 */
export async function getChapter(bookId: string, chapter: number): Promise<string | null> {
  const book = getBookById(bookId)
  if (!book) return null
  
  const reference = `${book.name} ${chapter}`
  return await getPassage(reference)
}

/**
 * Get verses from a reference range
 */
export async function getVerses(ref: BibleReference): Promise<BibleVerse[]> {
  const book = getBookById(ref.bookId)
  if (!book) return []
  
  const reference = formatReferenceForAPI(ref)
  const text = await getPassage(reference)
  
  if (!text) return []
  
  // Parse verses from the text - improved pattern matching
  const verses: BibleVerse[] = []
  
  // Pattern to match verse numbers at the start (handles both inline and newline verse numbers)
  const versePattern = /(?:^|\s)(\d+)\s+(.+?)(?=(?:^|\s)\d+\s+|$)/gm
  let match
  const matches: Array<{ verse: number; text: string }> = []
  
  while ((match = versePattern.exec(text)) !== null) {
    const verseNum = parseInt(match[1], 10)
    const verseText = match[2].trim()
    matches.push({ verse: verseNum, text: verseText })
  }
  
  // Filter by verse range if specified
  const startVerse = ref.startVerse
  const endVerse = ref.endVerse
  
  if (matches.length > 0) {
    matches.forEach(({ verse, text }) => {
      if (!startVerse || (verse >= startVerse && (!endVerse || verse <= endVerse))) {
        verses.push({
          bookId: ref.bookId,
          chapter: ref.chapter,
          verse,
          text,
        })
      }
    })
  } else {
    // If no verse numbers found, split by paragraphs or sentences
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0)
    paragraphs.forEach((para, idx) => {
      verses.push({
        bookId: ref.bookId,
        chapter: ref.chapter,
        verse: (startVerse || 1) + idx,
        text: para.trim(),
      })
    })
  }
  
  return verses
}

/**
 * Search for verses by keyword (simple implementation using topic mappings)
 * Note: ESV API doesn't have a search endpoint, so this uses predefined mappings
 */
export async function searchVersesByKeyword(_keyword: string): Promise<BibleSearchResult[]> {
  // This is a simplified search - in a real implementation, you might use
  // a different API or maintain a search index
  // For now, we'll return empty and let the topic mappings handle it
  return []
}

/**
 * Get book information
 */
export function getBookInfo(bookId: string) {
  return getBookById(bookId)
}

/**
 * Get all books
 */
export function getAllBooks() {
  return BIBLE_BOOKS
}

